import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, TemplateRef, ViewChild, output, input } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { ProfileDetails } from '../interfaces/myStories.interface';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModalComponent } from '../common-modal/common-modal.component';
import { SpinnerComponentComponent } from '../../modules/shared/components/spinner-component/spinner-component.component';
import { MyStoriesService } from '../../modules/stories/services/my-stories.service';
import { AppServicesService } from '../../services/app-services.service';
import { CommonModalServiceService } from '../../services/common-modal-service.service';
import { NotificationService, ToastType } from '../../services/notification.service';
import { TranslationService } from '../../services/translation.service';

@Component({
    selector: 'app-edit-profile',
    templateUrl: './edit-profile.component.html',
    styleUrls: ['./edit-profile.component.scss'],
    imports: [
        TranslateModule,
        SpinnerComponentComponent,
        CommonModalComponent,
        ReactiveFormsModule,
    ],
    standalone: true,
})
export class EditProfileComponent implements OnInit {
    constructor(
        private readonly commonModalService: CommonModalServiceService,
        private readonly fb: FormBuilder,
        public readonly _appService: AppServicesService,
        public readonly translationService: TranslationService,
        private readonly myStoriesService: MyStoriesService,
        private readonly notificationService: NotificationService,
    ) {}

    public editProfileForm!: FormGroup;
    @ViewChild('editPopup') editPopup: TemplateRef<any> | undefined;
    public readonly cancelClicked = output<any>();
    showEditPopup: boolean = true;
    isHeaderVisible: boolean = true;
    isCloseModal: boolean = false;
    showModalContent: boolean = true;
    profileName: string = '';
    popupType: string = 'large-popup';
    profileEmail: string = '';
    defaultBanner = '../../../assets/mercedes.png';
    profileImage = '';
    profileInitials: string = '';
    ciamId = '';
    isShowToast = false;
    position: any;
    leftPosition: any;
    translationData = [];
    getProfileResponse: boolean = false;
    isMbcLinked: boolean = false;
    readonly isStoriesSection = input<boolean>(false);
    readonly userId = input<any>(null as any);
    isErrorOccured: boolean = false;

    ngOnInit(): void {
        this.initializeEditProfileForm();
        this.configureModalDisplay();

        this.getUserDataOnLoad();
    }

    public initializeEditProfileForm(): void {
        this.editProfileForm = this.fb.group({
            nickName: [''],
            socialLink: [''],
            description: [''],
        });
    }

    public configureModalDisplay(): void {
        this.commonModalService._modalHeaderIsCloseBtnVisible.next(true);
        this.commonModalService._modalHeaderIsSaveBtnVisible.next(true);
        this.commonModalService._modalHeaderTitle.next('CP_LM_MAINPAGE_MYPROFILE');
    }

    public getUserDataOnLoad(): void {
        const userDetails = this.myStoriesService.profileInfoResponse;
        if (userDetails) {
            this.displayProfileInfo(userDetails);
        } else {
            this.myStoriesService.getProfile(this.userId()).subscribe((response: any) => {
                if (response) {
                    this.displayProfileInfo(response);
                }
            });
        }
    }

    public prePoulatedProfileForm(data: any): void {
        if (this.editProfileForm) {
            this.editProfileForm.patchValue({
                nickName: data.nickName,
                socialLink: data.socialLink,
                description: data.description,
            });
        }
    }

    public saveProfileData(): void {
        this.editProfileForm.patchValue({
            nickName: this.editProfileForm.value.nickName?.trim(),
            socialLink: this.editProfileForm.value.socialLink?.trim(),
            description: this.editProfileForm.value.description?.trim(),
        });
        if (this.editProfileForm.valid) {
            const reqObj = this.editProfileForm.value;
            reqObj.nickName = this.editProfileForm.value.nickName;
            reqObj.socialLink = this.editProfileForm.value.socialLink;
            reqObj.description = this.editProfileForm.value.description;
            this.myStoriesService
                .updateProfile(reqObj.nickName, reqObj.description, reqObj.socialLink, this.ciamId)
                .subscribe({
                    next: () => this.handleUpdateProfileResponse(reqObj),
                    error: (error: HttpErrorResponse) => this.updateErrorProfileResponse(error),
                });
        }
    }

    public handleUpdateProfileResponse(reqObj: any): void {
        if (
            this.myStoriesService.profileInfoResponse &&
            this.myStoriesService.profileInfoResponse.userId === this.ciamId
        ) {
            this.myStoriesService.profileInfoResponse.nickName = reqObj.nickName;
            this.myStoriesService.profileInfoResponse.socialLink = reqObj.socialLink;
            this.myStoriesService.profileInfoResponse.description = reqObj.description;
        }
        this.showEditPopup = false;
        this.cancelClicked.emit(true);
        this.notificationService.showNotification('CP_SM_TOAST_MSG_SUCCESS', ToastType.Success);
        this.myStoriesService.setIsProfileUpdate(true);
        this.myStoriesService.hideSpinner();
        setTimeout(() => {
            this.notificationService.closeNotification();
        }, 4000);
    }

    public closeModal(): void {
        this.showEditPopup = false;
        this.cancelClicked.emit(true);
    }

    public displayProfileInfo(data: ProfileDetails): void {
        this.getProfileResponse = true;
        this.profileName = data.firstName + ' ' + data.lastName;
        this.profileImage = data.profileImageUrl;
        this.profileEmail = data.email;
        this.profileInitials =
            data.firstName.charAt(0).toUpperCase() + data.lastName.charAt(0).toUpperCase();
        this.ciamId = data.userId;
        this.isMbcLinked = data.isMbcLinked;
        this.prePoulatedProfileForm(data);

        if (this.isMbcLinked) {
            this.editProfileForm.get('nickName')?.disable();
        } else {
            this.editProfileForm.get('nickName')?.enable();
        }
    }

    public updateErrorProfileResponse(error: HttpErrorResponse): void {
        this.isErrorOccured = true;
        this.myStoriesService.hideSpinner();
        const errorKey = this.getErrorKeyForProfile(error.error['error-code']);

        this.notificationService.showNotification(errorKey, ToastType.Error);
        setTimeout(() => {
            this.notificationService.closeNotification();
        }, 4000);
    }

    public getErrorKeyForProfile(code: string): string {
        const errors: any = {
            '9007': 'CP_SM_TOAST_MSG_NICKNAME_ERR',
            '9008': 'CP_SM_TOAST_MSG_DESC_ERR',
            '9009': 'CP_SM_TOAST_MSG_SOCLNK_ERR',
            '8005': 'CP_SM_TOAST_MSG_SOCLNK_INVALID',
        };
        const getErrorByCode = (code: string) => errors[code];
        return getErrorByCode(code);
    }
}
